import { Component, Inject, Input, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { Util, XmStore, XmStoreUtil } from 'core/services';
import { AccountSummary, LineSummary, PlanSummary } from 'store/account-summary/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';

@Component({
    selector: 'line-card',
    styleUrls: ['./line.scss'],
    templateUrl: './line.html',
    standalone: false
})
export class LineCardComponent implements OnInit {
    @Input() public lineCard: LineSummary;
    @Input() public plan: PlanSummary;
    @Input() public pageData: CmsLineCardData;

    public lineDataUsage: number;
    public progressWidth: string = '0%';
    public pairedLineNumber: string;
    public redirectLineKey: string;
    public uiParams: object;
    public isClickable: boolean = false;
    public ariaLabel: string;
    public enableStandaloneWatches: boolean = false;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private config: IXMOptions;

    constructor(xmStore: XmStore,  @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, config });
    }

    public ngOnInit(): void {
        this.enableStandaloneWatches = this.config.ENABLE_STANDALONE_WATCHES;
        this.lineDataUsage = parseFloat(Util.mbToGb(this.lineCard.dataUsage));
        this.redirectLineKey = this.lineCard.id.key;

        if (this.plan.isByTheGig) {
            const totalChargableGig: number = Math.ceil(parseFloat(Util.mbToGb(this.plan.dataUsage)));
            this.progressWidth = `${ (this.lineDataUsage / totalChargableGig) * 100 }%`;
        } else if (this.plan.isByTheGigShared) {
            const totalChargableSharedByTheGig: number = parseFloat(Util.mbToGb(this.plan.dataPassSize + this.plan.dataOverage));
            this.progressWidth = `${ (this.lineDataUsage / totalChargableSharedByTheGig) * 100 }%`;
        }

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek(AccountSummary), (accountSummary: AccountSummary) => {
            if (this.plan.isWatch && this.lineCard.pairedLines && this.lineCard.pairedLines.length && !this.lineCard.isStandaloneWatch) {
                const pairedLine: LineSummary = accountSummary.getLineByKey(this.lineCard.pairedLines[0].lineId.key);
                this.pairedLineNumber = pairedLine && pairedLine.lineNumber;
                this.uiParams = { line_id: pairedLine && pairedLine.id.key, '#': this.redirectLineKey };
                this.isClickable = Boolean(this.pairedLineNumber);
            } else if (!this.plan.isWatch || (this.plan.isWatch && this.lineCard.isStandaloneWatch && this.enableStandaloneWatches)) {
                this.uiParams = { line_id: this.redirectLineKey };
                this.isClickable = true;
            }

            this.ariaLabel = this.getAriaLabel();
        }));
    }

    public onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            (<HTMLElement>event.currentTarget).click();
        }
    }

    private getAriaLabel(): string {
        let label: string = this.formatForSpeech(this.lineCard.displayName);
        if (this.lineCard.isActive && !this.plan.isWatch) {
            label = `${label} ${this.formatForSpeech(this.lineCard.lineNumber)}`;
        }

        if (this.lineCard.isActive && this.plan.isWatch) {
            label = `${label} paired with ${this.formatForSpeech(this.pairedLineNumber)}`;
        }

        if (!this.lineCard.isActive) {
            label = `${label} ${this.lineCard.displayStatus} line`;
        }

        if (!this.plan.isWatch) {
            label = `${label} used ${this.lineDataUsage} GB of data`;
        }

        return label;
    }

    private formatForSpeech(value: string | number): string {
        return String(value).replace(/\d/g, ' $& ').replace(/\s+/g, ' ').trim();
    }
}
